/**
 * Dimension utilities for artwork scaling
 * Converts physical dimensions (inches) to A-Frame units (meters)
 */

export const DIMENSION_SCALING_CONFIG = {
  ENABLED: true,
  MAX_DIMENSION_METERS: 10, // 394 inches max
  MIN_DIMENSION_METERS: 0.05, // 2 inches min
  PRESERVE_ASPECT_RATIO: true, // Preserve image aspect ratio when metadata differs
  WARN_ON_ASPECT_MISMATCH: true // Log warnings when aspect ratios don't match
};

/**
 * Converts inches to A-Frame units (meters)
 * @param {number} inches - Dimension in inches
 * @param {number} unitsPerMeter - Scale factor (default: 1)
 * @returns {number} Dimension in meters
 */
export function inchesToUnits(inches, unitsPerMeter = 1) {
  if (typeof inches !== "number" || isNaN(inches)) {
    console.warn("[dimension-utils] Invalid inches value:", inches);
    return null;
  }
  return inches * 0.0254 * unitsPerMeter;
}

/**
 * Validates artwork dimensions before applying
 * @param {number} widthInches - Width in inches
 * @param {number} heightInches - Height in inches
 * @param {number} depthInches - Depth in inches (optional)
 * @returns {object} Validation result with {valid, issues, dimensions}
 */
export function validateDimensions(widthInches, heightInches, depthInches = null) {
  const width = inchesToUnits(widthInches);
  const height = inchesToUnits(heightInches);
  const depth = depthInches ? inchesToUnits(depthInches) : null;

  const issues = [];

  // Check for null/NaN values
  if (width === null) {
    issues.push(`Invalid width: ${widthInches}`);
  }
  if (height === null) {
    issues.push(`Invalid height: ${heightInches}`);
  }

  // Validate width
  if (width !== null) {
    if (width > DIMENSION_SCALING_CONFIG.MAX_DIMENSION_METERS) {
      issues.push(
        `Width ${widthInches}" (${width.toFixed(2)}m) exceeds maximum ${DIMENSION_SCALING_CONFIG.MAX_DIMENSION_METERS}m`
      );
    }
    if (width < DIMENSION_SCALING_CONFIG.MIN_DIMENSION_METERS) {
      issues.push(
        `Width ${widthInches}" (${width.toFixed(2)}m) below minimum ${DIMENSION_SCALING_CONFIG.MIN_DIMENSION_METERS}m`
      );
    }
  }

  // Validate height
  if (height !== null) {
    if (height > DIMENSION_SCALING_CONFIG.MAX_DIMENSION_METERS) {
      issues.push(
        `Height ${heightInches}" (${height.toFixed(2)}m) exceeds maximum ${DIMENSION_SCALING_CONFIG.MAX_DIMENSION_METERS}m`
      );
    }
    if (height < DIMENSION_SCALING_CONFIG.MIN_DIMENSION_METERS) {
      issues.push(
        `Height ${heightInches}" (${height.toFixed(2)}m) below minimum ${DIMENSION_SCALING_CONFIG.MIN_DIMENSION_METERS}m`
      );
    }
  }

  // Validate depth (if provided)
  if (depth !== null && depth > DIMENSION_SCALING_CONFIG.MAX_DIMENSION_METERS) {
    issues.push(
      `Depth ${depthInches}" (${depth.toFixed(2)}m) exceeds maximum ${DIMENSION_SCALING_CONFIG.MAX_DIMENSION_METERS}m`
    );
  }

  return {
    valid: issues.length === 0 && width !== null && height !== null,
    issues,
    dimensions: { width, height, depth }
  };
}
